import type { BazelBuildLog, BazelTests } from '@/models/BazelInvocationArtifacts';
import { BuildCacheToolIds } from '@/models/BuildCacheTool';
import { BuildToolInvocationGradleCriticalPathTask } from '@/models/BuildToolInvocationGradleTask';
import { CiProviderId } from '@/models/CiProvider';
import GitProvider from '@/models/GitProvider';

type CcacheDiagnosticsCountTotal = {
  count: number;
  total: number;
  percentage: number;
};

type CcacheDiagnosticsCountOnly = {
  count: number;
  percentage: number;
};

export type CcacheDiagnostics = {
  summary: {
    totalCallCount: number;
    cacheableCallCount: number;
    uncacheableCallCount: number;
  };
  cacheHitsAndMisses: {
    cacheHits: CcacheDiagnosticsCountTotal;
    directHits: CcacheDiagnosticsCountTotal;
    preprocessedHits: CcacheDiagnosticsCountTotal;
    cacheMisses: CcacheDiagnosticsCountTotal;
  };
  remoteStorage: {
    hits: CcacheDiagnosticsCountTotal;
    misses: CcacheDiagnosticsCountTotal;
    errors: CcacheDiagnosticsCountOnly;
    timeouts: CcacheDiagnosticsCountOnly;
  };
  configuration: Array<{ key: string; value: string }>;
};

export enum BuildToolInvocationStatus {
  success = 'success',
  failed = 'failed',
  pending = 'pending',
  timeout = 'timeout',
}

// Status of the Bazel command profile (`command.profile.gz`) for an invocation.
// Only `saved` is downloadable; `expired` means the GCS lifecycle already deleted it.
export enum BazelCommandProfileStatus {
  pending = 'pending',
  saved = 'saved',
  failed = 'failed',
  expired = 'expired',
}

// Ordered by Bazel's event stream, not wall clock, so the first entry is the failure that
// happened first — under `--keep_going` the later ones are usually fallout from it.
export type BazelFailedAction = {
  label?: string;
  mnemonic?: string;
  message?: string;
  // Absent has two causes needing different wording: no output ever existed because the action
  // failed before it ran, or output existed and we do not have it (withheld for a build that
  // declared secrets, or evicted before the worker read it). Never "the action ran and was quiet".
  stderr?: string;
  exitCode?: number;
  // Approximate: Bazel doesn't order an action's output relative to its completion event.
  logLine?: number;
};

// Present without any failed action when the build never got as far as running one.
export type BazelAbort = {
  reason: string;
  description?: string;
};

export type BuildToolInvocation = {
  invocationId: string;
  tool: BuildCacheToolIds;
  toolVersion?: string;
  usesCache: boolean;
};

export type BuildToolInvocationWithDetails = BuildToolInvocation & {
  bazelAborted?: BazelAbort[] | null;
  bazelBuildLog?: BazelBuildLog | null;
  bazelTests?: BazelTests | null;
  bazelCachedActionCount?: number | null;
  // Bazel's own verdict on the build, e.g. BUILD_FAILURE / REMOTE_CACHE_EVICTED. Scoped to the
  // invocation, not to any single failed action.
  bazelExitName?: string | null;
  bazelFailedActions?: BazelFailedAction[] | null;
  // Bazel's own explanation of why the build failed. The only structured signal when the build died
  // before any action ran, which is exactly when bazelFailedActions is empty.
  bazelFailureDetail?: string | null;
  bazelTotalActionCount?: number | null;
  commandProfileStatus?: BazelCommandProfileStatus | null;
  branchName?: string;
  branchUrl?: string;
  buildNumber?: number;
  buildSlug?: string;
  cacheDownloadBytes?: number | null;
  cacheHitRate: number | null;
  cacheUploadBytes?: number | null;
  ciProvider: CiProviderId;
  ciRunUrl?: string;
  command: string;
  commitHash?: string;
  commitUrl?: string;
  duration: number;
  // GITHUB_RUN_ID / CI_PIPELINE_ID. Not the run number GitHub's own UI shows — that is not captured.
  externalBuildId?: string;
  // GITHUB_JOB / CI_JOB_NAME — the job key from the CI config, not the workflow or pipeline name.
  externalWorkflowName?: string;
  failedTaskPath?: string | null;
  failureReason?: string | null;
  gitRepositoryProvider?: GitProvider;
  gitRepositoryTitle?: string;
  gitRepositoryWebUrl?: string;
  gradleCriticalPath?: {
    tasks: BuildToolInvocationGradleCriticalPathTask[];
    firstExecutedTaskId: string | null;
  } | null;
  hasAnyChildInvocation?: boolean;
  hasMeaningfulExecutionReasons?: boolean | null;
  parentInvocation?: {
    invocationId: string;
    buildTool: BuildCacheToolIds;
    command: string;
  };
  projectSlug?: string;
  projectTitle: string;
  repositoryUrl?: string;
  runsOnBuildHub?: boolean;
  shortCommand?: string;
  startedAt: string;
  status: BuildToolInvocationStatus;
  stepExecutionId?: string;
  targetOs?: ('ios' | 'android')[];
  toolWithVersionInfo: string;
  traceToken: string;
  username?: string;
  workflowName?: string;
  wrapper?: string;
  benchmarkPhase?: string;
  ccacheDiagnostics?: CcacheDiagnostics;
  taskStatistics?: {
    totalTaskCount: number;
    upToDateTaskCount: number;
    fromCacheTaskCount: number;
    actionableTaskCount: number;
    cacheableTaskCount: number;
  } | null;
};
